/**
 * RunJobSheet — the reusable, minimum "Run this template" surface.
 *
 * Opens as a right-side Sheet anywhere a "Run" affordance lives (the Jobs
 * page card grid, Job Detail "Run again", Library template detail). The
 * 80%-case form: inputs + assigned member + Start. No title rename, no
 * instructions override, no remember-outputs toggle — those live on the
 * full-page version at /jobs/new and are reachable via "More options →".
 *
 * Two input modes:
 *   - Template has input_schema → SchemaForm. Attachment fields render
 *     inside the schema (via x-linqage-attachment). No separate uploads.
 *   - No schema → free-text textarea + ChatAttachmentPicker. Both are
 *     promoted to the minimum because without them the agent has nothing.
 *
 * Top-level "Supporting Documents" (attachment_ids regardless of schema)
 * is a power-user concept and lives only on the full page.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/Alert/Alert';
import { Button } from '@/components/Button/Button';
import { InfoHint } from '@/components/InfoHint/InfoHint';
import { SchemaForm } from '@/components/SchemaForm/SchemaForm';
import type { JsonSchema } from '@/components/SchemaForm/types';
import { Sheet } from '@/components/Sheet/Sheet';
import { Tabs } from '@/components/Tabs/Tabs';
import { useJobTemplateDetail } from '@/features/library';
import { useUploadAttachment } from '@/features/attachments';
import { useInfoHintPulse } from '@/features/onboarding';
import type { PendingAttachment } from '@/features/members/components/ChatAttachmentPicker';
import { t } from '@/lib/i18n';
import { useCreateJob } from '../hooks';
import type { CreateJobBody } from '../schemas';
import { JobAttachmentTable } from './JobAttachmentTable';
import { PendingAddBanner } from './PendingHireBanner';
import { RecommendedMembersPanel, type AssigneeSelection } from './RecommendedMembersPanel';
import styles from './RunJobSheet.module.css';

const FORM_ID = 'run-job-sheet-form';

function hasSchemaFields(schema: JsonSchema | null | undefined): boolean {
  return Boolean(schema?.properties && Object.keys(schema.properties).length > 0);
}

export interface RunJobSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The template to run. Sheet content is empty until this is set. */
  templateId: string | null;
  /** Optional pre-filled inputs (used by "Run again"). */
  prefillInputs?: Record<string, unknown>;
  /** Optional pre-selected Member id (used by "Run again" or ?member= deep-link). */
  prefillMemberId?: string | null;
}

export function RunJobSheet({
  open,
  onOpenChange,
  templateId,
  prefillInputs,
  prefillMemberId,
}: RunJobSheetProps) {
  const navigate = useNavigate();
  const { data: templateResponse, isError: templateError } = useJobTemplateDetail(
    templateId ?? undefined,
  );
  const template = templateResponse?.item ?? null;
  const create = useCreateJob();
  const upload = useUploadAttachment();
  const pulsing = useInfoHintPulse();

  const [assignee, setAssignee] = useState<AssigneeSelection>(
    prefillMemberId ? { kind: 'member', id: prefillMemberId } : null,
  );
  const [freeText, setFreeText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset state when the user opens the sheet against a new template, or
  // closes-and-reopens against the same one. Without this, stale assignee
  // / free-text leaks between runs.
  useEffect(() => {
    if (open) {
      setAssignee(prefillMemberId ? { kind: 'member', id: prefillMemberId } : null);
      setFreeText('');
      setPendingAttachments([]);
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const inputSchema = (template?.inputSchema as JsonSchema | null) ?? null;
  const schemaHasFields = hasSchemaFields(inputSchema);
  const isFreeForm = !inputSchema;
  const willAdd = assignee?.kind === 'staff_profile';
  const hasPendingUploads = pendingAttachments.some((p) => !p.attachment && !p.error);
  const attachmentIds = pendingAttachments.filter((p) => p.attachment).map((p) => p.attachment!.id);
  const canSubmit = Boolean(assignee) && !create.isPending && !hasPendingUploads;
  const startLabel = willAdd ? t('Add & Start') : t('Start');

  async function handleSubmit(inputs: Record<string, unknown>) {
    if (!templateId || !assignee) {
      setSubmitError(t('Pick a team member before starting this job.'));
      return;
    }
    setSubmitError(null);
    const body: CreateJobBody = {
      job_template_id: templateId,
      ...(assignee.kind === 'member'
        ? { assigned_member_id: assignee.id }
        : { staff_profile_id: assignee.id }),
      inputs,
    };
    // Free-form templates can't carry input-schema attachments, so the
    // user's drop-zone uploads ride on attachment_ids instead.
    if (isFreeForm && attachmentIds.length > 0) {
      body.attachment_ids = attachmentIds;
    }
    try {
      const job = await create.mutateAsync(body);
      onOpenChange(false);
      void navigate(`/jobs/${job.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('Failed to start the job.'));
    }
  }

  function openFullPage() {
    if (!templateId) return;
    onOpenChange(false);
    // Preserve in-flight form state so the user doesn't lose work moving
    // from the sheet to the full page.
    const state = {
      templateId,
      assignee,
      freeText,
      prefillInputs: prefillInputs ?? null,
    };
    void navigate(`/jobs/new?template=${encodeURIComponent(templateId)}`, { state });
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{template?.title ?? t('Run a Job')}</span>
          <InfoHint
            label={t('Run a Job')}
            tooltip={t("Set the inputs and pick who runs it. Hit Start when you're ready.")}
            pulsing={pulsing}
          />
        </div>
      }
      description={template?.description ?? undefined}
      footer={
        <>
          <button type="button" className={styles.moreOptions} onClick={openFullPage}>
            {t('More options →')}
          </button>
          <span className={styles.spacer} />
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={!canSubmit}
            isLoading={create.isPending}
          >
            {startLabel}
          </Button>
        </>
      }
    >
      {templateError ? (
        <p className={styles.loading}>{t('Unable to load job details.')}</p>
      ) : !template ? (
        <p className={styles.loading}>{t('Loading…')}</p>
      ) : (
        <>
          {submitError ? (
            <Alert variant="error" onDismiss={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          ) : null}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('Inputs')}</h3>
            {template.inputsDescription ? (
              <p className={styles.sectionHelp}>{template.inputsDescription}</p>
            ) : null}
            {schemaHasFields && inputSchema ? (
              <SchemaForm
                schema={inputSchema}
                formId={FORM_ID}
                hideSubmit
                defaultValues={prefillInputs}
                onSubmit={(values) => {
                  void handleSubmit(values);
                }}
                onUploadAttachment={async (file) => {
                  const res = await upload.mutateAsync({ file });
                  return { attachment_id: res.attachment.id };
                }}
                isPending={create.isPending || hasPendingUploads}
              />
            ) : (
              <form
                id={FORM_ID}
                className={styles.freeForm}
                onSubmit={(e) => {
                  e.preventDefault();
                  const inputs = freeText.trim() ? { text: freeText } : {};
                  void handleSubmit(inputs);
                }}
              >
                <Tabs.Root defaultValue="files" className={styles.tabsRoot}>
                  <Tabs.List>
                    <Tabs.Trigger value="files">
                      {t('Files')}
                      {attachmentIds.length > 0 ? (
                        <span className={styles.tabCount}>{attachmentIds.length}</span>
                      ) : null}
                    </Tabs.Trigger>
                    <Tabs.Trigger value="text">{t('Text')}</Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value="files">
                    <JobAttachmentTable
                      attachments={pendingAttachments}
                      onChange={setPendingAttachments}
                    />
                  </Tabs.Content>
                  <Tabs.Content value="text">
                    <textarea
                      className={styles.freeTextArea}
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      placeholder={t('Add additional context here')}
                      rows={6}
                      aria-label={t('Additional context')}
                    />
                  </Tabs.Content>
                </Tabs.Root>
              </form>
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('Assigned Member')}</h3>
            {!assignee ? (
              <div className={styles.assigneePlaceholder} role="status">
                <span className={styles.assigneePlaceholderIcon} aria-hidden>
                  👤
                </span>
                <span>
                  {t(
                    'Pick a recommended team member below, or expand "Other team members" to choose from your whole team.',
                  )}
                </span>
              </div>
            ) : null}
            <RecommendedMembersPanel
              templateId={templateId}
              value={assignee}
              onChange={setAssignee}
            />
            {willAdd && assignee ? (
              <PendingAddBanner staffProfileId={assignee.id} templateId={templateId} />
            ) : null}
          </section>
        </>
      )}
    </Sheet>
  );
}
